import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { createTeamWeatherData, getTeamWeatherData, deleteTeamWeatherData } from '../api';
import WeatherDataForm from '../components/WeatherDataForm';

export default function TeamWeatherDataSection({ team }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    location: '',
    snow_temperature_c: '',
    air_temperature_c: '',
    snow_humidity_doser: '',
    air_humidity_rh: '',
    clouds_sky_parts: '',
    visibility: '',
    wind: '',
    precipitation: '',
    artificial_snow: '',
    natural_snow: '',
    grain_size: '',
    snow_humidity: '',
    track_hardness: '',
    track_shininess: '',
    track_dirtiness: '',
    notes: ''
  });

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTeamWeatherData(team.id);
      setEntries(data);
    } catch (err) {
      console.error('Failed to load weather data entries:', err);
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        date: entry.date ? entry.date.split('T')[0] : '',
        location: entry.location || '',
        snow_temperature_c: entry.snow_temperature_c ?? '',
        air_temperature_c: entry.air_temperature_c ?? '',
        snow_humidity_doser: entry.snow_humidity_doser ?? '',
        air_humidity_rh: entry.air_humidity_rh ?? '',
        clouds_sky_parts: entry.clouds_sky_parts ?? '',
        visibility: entry.visibility || '',
        wind: entry.wind || '',
        precipitation: entry.precipitation || '',
        artificial_snow: entry.artificial_snow || '',
        natural_snow: entry.natural_snow || '',
        grain_size: entry.grain_size || '',
        snow_humidity: entry.snow_humidity || '',
        track_hardness: entry.track_hardness || '',
        track_shininess: entry.track_shininess || '',
        track_dirtiness: entry.track_dirtiness || '',
        notes: entry.notes || ''
      });
    } else {
      setEditingEntry(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        location: '',
        snow_temperature_c: '',
        air_temperature_c: '',
        snow_humidity_doser: '',
        air_humidity_rh: '',
        clouds_sky_parts: '',
        visibility: '',
        wind: '',
        precipitation: '',
        artificial_snow: '',
        natural_snow: '',
        grain_size: '',
        snow_humidity: '',
        track_hardness: '',
        track_shininess: '',
        track_dirtiness: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEntry(null);
  };

  const handleSave = async () => {
    if (!formData.date) {
      alert('Date is required');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        snow_temperature_c: formData.snow_temperature_c === '' ? null : parseFloat(formData.snow_temperature_c),
        air_temperature_c: formData.air_temperature_c === '' ? null : parseFloat(formData.air_temperature_c),
        snow_humidity_doser: formData.snow_humidity_doser === '' ? null : parseInt(formData.snow_humidity_doser),
        air_humidity_rh: formData.air_humidity_rh === '' ? null : parseFloat(formData.air_humidity_rh),
        clouds_sky_parts: formData.clouds_sky_parts === '' ? null : parseInt(formData.clouds_sky_parts)
      };

      await createTeamWeatherData(team.id, dataToSave);
      await loadEntries();
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to save weather data entry:', err);
      alert('Failed to save weather data entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this weather data entry?')) {
      return;
    }
    try {
      await deleteTeamWeatherData(team.id, entryId);
      await loadEntries();
    } catch (err) {
      console.error('Failed to delete weather data entry:', err);
      alert('Failed to delete weather data entry');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Team Weather Data</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Weather Data Entry
        </Button>
      </Box>

      {entries.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No weather data entries yet. Create one to help athletes pre-fill weather data when adding races and tests.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Air Temp (°C)</TableCell>
                <TableCell>Snow Temp (°C)</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>{entry.location || '-'}</TableCell>
                  <TableCell>{entry.air_temperature_c ?? '-'}</TableCell>
                  <TableCell>{entry.snow_temperature_c ?? '-'}</TableCell>
                  <TableCell>{entry.created_by_name || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(entry.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Edit Weather Data Entry' : 'Add Weather Data Entry'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <TextField
              label="Location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              fullWidth
            />
            <WeatherDataForm
              weatherData={formData}
              onChange={setFormData}
              title="Weather Conditions"
              compact={true}
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

